<template>
    <div>
      <v-container fluid>
        <v-card elevation="0">
          <div class="d-flex mb-2 pt-10">
            <h2>
              {{ $t("deforestation.complianceStatusReview") }}
              <v-tooltip top color="00BD73" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="icon-with-background primary--text" style="color:#0EBF67" v-bind="attrs" v-on="on">
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ $t("deforestation.complianceStatusReviewTT") }}</span>
              </v-tooltip>
            </h2>
            <v-spacer />
          </div>
          <p class="text-body-2 grey--text text--darken-1 mb-6">
            {{ $t("deforestation.complianceStatusReviewDesc") }}
          </p>
  
          <div class="d-flex mb-4 pt-2">
            <h3 class="text-h6">{{ $t("deforestation.productionPlaces") }}</h3>
            <v-spacer />
          </div>

          <v-dialog v-model="wildfireBulkDialog" max-width="480" persistent>
            <v-card rounded="0">
              <v-toolbar color="primary" class="white--text" flat :elevation="0">
                <v-toolbar-title class="font-weight-bold">
                  {{ $t('deforestation.wildfireBulkDialogTitle') }}
                </v-toolbar-title>
              </v-toolbar>
              <v-card-text class="pt-5">
                {{ $t('deforestation.wildfireBulkConfirmMessage', { count: totalProductionPlaces }) }}
              </v-card-text>
              <v-card-actions class="justify-end pb-4 px-4">
                <v-btn text @click="closeWildfireBulkDialog">
                  {{ $t('deforestation.wildfireBulkCancel') }}
                </v-btn>
                <v-btn color="primary" depressed @click="startWildfireBulkGeneration">
                  {{ $t('deforestation.wildfireBulkStart') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <ProcessingDialog
            :dialog="wildfireBulkPhase !== null && wildfireBulkProcessingVisible"
            :heading="$t('deforestation.wildfireBulkDialogTitle')"
            :description="wildfireBulkProcessingDescription"
            :show-close-button="wildfireBulkPhase === 'error'"
            :has-timer="wildfireBulkIsAnalyzing"
            :timer-seconds="wildfireBulkTimerSeconds"
            @close="closeWildfireBulkDialog"
          >
            <v-icon v-if="wildfireBulkPhase === 'error'" color="error" :size="100">mdi-alert-circle</v-icon>
            <v-progress-circular v-else color="primary" indeterminate :size="100" />
          </ProcessingDialog>

          <v-dialog v-model="wildfireBulkResultsDialog" max-width="760" scrollable>
            <v-card>
              <v-card-title class="text-body-1 font-weight-bold d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-fire-alert</v-icon>
                {{ $t('deforestation.wildfireBulkDialogTitle') }}
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4">
                <div class="d-flex align-center">
                  <v-progress-linear rounded height="8" :value="wildfireBulkSuccessPercent" color="primary" />
                  <span class="primary--text font-weight-bold ml-2">{{ wildfireBulkSuccessPercent }}%</span>
                </div>
                <div class="d-flex justify-space-between grey lighten-4 py-3 px-4 mt-3 mb-3" style="border-radius:8px;">
                  <div class="d-flex align-center flex-column">
                    <span class="text-h4">{{ wildfireBulkSummary ? wildfireBulkSummary.total : 0 }}</span>
                    <span class="text-body-2">{{ $t('totalRecords') }}</span>
                  </div>
                  <div class="d-flex align-center flex-column">
                    <span class="text-h4 red--text">{{ wildfireBulkSummary ? wildfireBulkSummary.failed : 0 }}</span>
                    <span class="text-body-2">{{ $t('failed') }}</span>
                  </div>
                  <div class="d-flex align-center flex-column">
                    <span class="text-h4 primary--text">{{ wildfireBulkSummary ? wildfireBulkSummary.succeeded : 0 }}</span>
                    <span class="text-body-2">{{ $t('success') }}</span>
                  </div>
                </div>

                <v-card outlined style="max-height:360px; overflow:auto;">
                  <v-data-table
                    disable-pagination
                    hide-default-footer
                    dense
                    :headers="wildfireBulkResultHeaders"
                    :items="wildfireBulkResults"
                  >
                    <template v-slot:item.success="{ item }">
                      <v-chip
                        small outlined class="justify-center"
                        style="width:90px;"
                        :color="item.success ? 'primary' : 'error'"
                      >
                        {{ item.success ? $t('success') : $t('failed') }}
                      </v-chip>
                    </template>
                    <template v-slot:item.error="{ item }">
                      <v-tooltip bottom :disabled="!item.error" max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs" v-on="on"
                            class="caption red--text d-inline-block text-truncate"
                            style="max-width:260px; cursor:default;"
                          >{{ item.error || '—' }}</span>
                        </template>
                        <span>{{ item.error }}</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-card-text>
              <v-divider />
              <v-card-actions class="justify-end">
                <v-btn color="primary" depressed @click="wildfireBulkResultsDialog = false">
                  {{ $t('deforestation.wildfireBulkClose') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <div class="production-places-block mb-4">
          <div
            v-if="isPalmOilCommodity"
            class="d-flex align-center flex-wrap mb-2 py-3 px-4 production-places-wildfire-bulk"
            style="background:#F0F8FF; border-radius:10px; border:1px solid #90CAF9; gap:12px;"
          >
            <div class="d-flex align-center" style="gap:8px; flex:1; min-width:200px;">
              <v-icon color="#0288D1" small>mdi-fire-alert</v-icon>
              <span class="text-body-2 grey--text text--darken-2">
                {{ $t('deforestation.wildfireBulkHint') }}
              </span>
            </div>
            <v-btn v-if="!wildfireBulkIsAnalyzing" medium color="primary" depressed @click="openWildfireBulkDialog">
              <v-icon medium :style="{color: '#ffffff !important'}" class="mr-1">mdi-fire-alert</v-icon>
              {{ $t('deforestation.wildfireBulkGenerateButton') }}
            </v-btn>
            <v-btn v-else small rounded min-width="160" height="34" depressed class="gray-button">
              <v-icon small class="mr-1">mdi-progress-clock</v-icon>
              {{ $t('deforestation.wildfireBulkAnalyzing') }}
            </v-btn>
          </div>
          <v-alert v-if="darkRedFarmCount > 0" type="error" dense text class="mb-2">
            <strong>{{ $t('deforestation.complianceTrafficLightCriticalRiskCount', { count: darkRedFarmCount }) }}</strong>
            {{ $t('deforestation.complianceTrafficLightCriticalRiskMustRemove') }}
          </v-alert>
          <v-alert
            v-if="lightRedFarmCount > 0 || yellowFarmCount > 0"
            dense
            outlined
            class="mb-2 production-places-advisory"
            color="grey lighten-4"
          >
            <div class="d-flex align-start" style="gap:8px;">
              <v-icon color="warning" small class="mt-1">mdi-alert</v-icon>
              <span class="text-body-2 grey--text text--darken-2">
                {{ $t('dueDiligence.reviewProductionPlaceRiskAdvisory') }}
              </span>
            </div>
          </v-alert>

          <div
            v-if="unmitigatedLightRedCount > 0 || unmitigatedYellowCount > 0"
            class="d-flex align-center flex-wrap mb-4 py-3 px-4 production-places-bulk-upload"
            style="background:#FFFBF0; border-radius:10px; border:1px solid #FFCC80; gap:12px;"
          >
            <div class="d-flex align-center" style="gap:8px; flex:1; min-width:200px;">
              <v-icon color="primary" small>mdi-file-upload-outline</v-icon>
              <span class="text-body-2 grey--text text--darken-2">
                {{ $t('deforestation.complianceTrafficLightBulkUploadHint') }}
              </span>
            </div>
            <v-btn small color="primary" depressed @click="openAttachDocDialog">
              <v-icon small :style="{color: '#ffffff !important'}" class="mr-1">mdi-file-upload-outline</v-icon>
              {{ $t('deforestation.complianceTrafficLightUploadMitigationDocument') }}
            </v-btn>
          </div>
          </div>
  
          <v-data-table
            :headers="tableHeaders"
            :items="filterData"
            :items-per-page="options.limit"
            hide-default-footer
            :loading="loading"
            :options.sync="options"
            :loading-text="$t('deforestation.loadingProductionPlaces')"
          >
            <template v-slot:top>
              <div class="filter-controls-bar d-none d-lg-flex mt-4 mb-2">
                <div class="filter-controls-left">
                  <v-text-field
                    prepend-inner-icon="mdi-magnify"
                    v-model="search"
                    :label="$t('search')"
                    dense outlined hide-details
                    class="filter-field filter-field--search"
                    @keyup.enter="searchQuery"
                  />
                  <!-- <v-autocomplete
                    v-model="statusModel"
                    :items="allStatus"
                    item-text="text" item-value="val"
                    :label="$t('deforestation.EUDRDeforestationStatus')"
                    dense outlined hide-details clearable
                    class="filter-field filter-field--status"
                    @change="getProductionPlaces"
                  /> -->
                </div>
                <div class="filter-controls-right">
                  <span class="pagination-info">{{ from }} - {{ to }} of {{ totalProductionPlaces }}</span>
                  <v-btn fab small outlined color="primary" :disabled="options.page <= 1" @click="pageChange(false)">
                    <v-icon dark>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn fab small outlined color="primary" :disabled="options.page >= totalPages" @click="pageChange(true)">
                    <v-icon dark>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </template>
  
            <template v-if="filterData.length" v-slot:body="{ items }">
              <tbody>
                <tr v-for="item in items" :key="item.id">
                  <td class="text-truncate">
                    {{ item.farm.farmName || "N/A" }}<br />
                    {{ item.farm.id }}
                  </td>
                  <td class="text-truncate">
                    {{ item.farm.userDdsAssoc.firstName }}
                    {{ item.farm.userDdsAssoc.lastName }}
                  </td>
                  <td class="text-truncate" style="max-width: 280px;">
                    {{ item.farm.address }}
                  </td>
                  <td class="text-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on" @click.stop="openTrafficLightDialog(item)">
                          <v-icon :style="'color: ' + demoTrafficLightColor(item) + ' !important'">
                            {{ demoTrafficLightIcon(item) }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("deforestation.viewComplianceIndicator") }}</span>
                    </v-tooltip>
                    <div class="text-caption font-weight-medium mt-1" :style="{ color: demoTrafficLightColor(item) }">
                      {{ trafficLightLabel(item) }}
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="production-place-actions-grid">
                      <div class="production-place-actions__cell">
                        <template v-if="getTrafficLight(item) === 'LIGHT_RED'">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn x-small fab v-bind="attrs" v-on="on" class="production-place-actions__btn"
                                :style="item.hasMitigationDocs ? 'background:#489E16 !important;' : 'background:#DA3D3D !important;'"
                                @click.stop="openSingleFarmMitigation(item)">
                                <v-icon small style="color:white!important">
                                  {{ item.hasMitigationDocs ? 'mdi-check' : 'mdi-file-upload' }}
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>{{ item.hasMitigationDocs ? $t('deforestation.complianceTrafficLightMitigationUploaded') : $t('deforestation.complianceTrafficLightUploadMitigationRequired') }}</span>
                          </v-tooltip>
                        </template>
                        <template v-else-if="getTrafficLight(item) === 'YELLOW'">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn x-small fab v-bind="attrs" v-on="on" class="production-place-actions__btn"
                                :style="item.hasMitigationDocs ? 'background:#489E16 !important;' : 'background:#FFA826 !important;'"
                                @click.stop="openSingleFarmMitigation(item)">
                                <v-icon small style="color:white!important">
                                  {{ item.hasMitigationDocs ? 'mdi-check' : 'mdi-file-upload-outline' }}
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>{{ item.hasMitigationDocs ? $t('deforestation.complianceTrafficLightMitigationUploaded') : $t('deforestation.complianceTrafficLightUploadMitigationDocument') }}</span>
                          </v-tooltip>
                        </template>
                      </div>
                      <div class="production-place-actions__cell">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn x-small fab color="primary" class="production-place-actions__btn"
                              v-bind="{...attrs, ...getButtonProps()}" v-on="on" @click="removeFarm(item.farmId)">
                              <v-icon small style="color: white !important;">mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('delete') }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
            <template v-slot:footer />
          </v-data-table>
  
          <div class="traffic-light-legend mt-4">
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker">
                <v-icon class="traffic-light-legend__marker-icon" :style="{ color: '#489E16 !important' }">mdi-check-circle</v-icon>
              </span>
              <span class="text-caption">{{ $t('deforestation.zeroOrNegligibleRisk') }}</span>
            </div>
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker">
                <v-icon class="traffic-light-legend__marker-icon" :style="{ color: '#FFA826 !important' }">mdi-alert-circle</v-icon>
              </span>
              <span class="text-caption">{{ $t('deforestation.combinedComplianceMediumRisk') }}</span>
            </div>
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker">
                <v-icon class="traffic-light-legend__marker-icon" :style="{ color: '#DA3D3D !important' }">mdi-close-circle</v-icon>
              </span>
              <span class="text-caption">{{ $t('deforestation.riskStatusHigh') }}</span>
            </div>
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker">
                <v-icon class="traffic-light-legend__marker-icon" :style="{ color: '#7B0000 !important' }">mdi-close-octagon</v-icon>
              </span>
              <span class="text-caption">{{ $t('deforestation.complianceTrafficLightLegendCriticalRisk') }}</span>
            </div>
            <span class="traffic-light-legend__divider" />
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker traffic-light-legend__marker--badge traffic-light-legend__marker--yellow">
                <v-icon class="traffic-light-legend__marker-icon traffic-light-legend__marker-icon--inverse">mdi-file-upload-outline</v-icon>
              </span>
              <span class="text-caption">{{ $t('dueDiligence.legendMitigationRecommended') }}</span>
            </div>
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker traffic-light-legend__marker--badge traffic-light-legend__marker--red">
                <v-icon class="traffic-light-legend__marker-icon traffic-light-legend__marker-icon--inverse">mdi-file-upload</v-icon>
              </span>
              <span class="text-caption">{{ $t('dueDiligence.legendMitigationRequired') }}</span>
            </div>
            <div class="traffic-light-legend__entry">
              <span class="traffic-light-legend__marker traffic-light-legend__marker--badge traffic-light-legend__marker--green">
                <v-icon class="traffic-light-legend__marker-icon traffic-light-legend__marker-icon--inverse">mdi-check</v-icon>
              </span>
              <span class="text-caption">{{ $t('dueDiligence.legendMitigationUploaded') }}</span>
            </div>
          </div>
        </v-card>
      </v-container>
  
      <confirm-box
        :title="farmConfirmDelete.title"
        :message="farmConfirmDelete.message"
        :cancelBtnText="farmConfirmDelete.cancelBtnText"
        :confirmBtnText="farmConfirmDelete.confirmBtnText"
        :confirmData="farmConfirmDelete.confirmData"
        :dialog="farmConfirmDeleteDialog"
        @cancel:action="farmConfirmDeleteDialog = false"
        @continue:action="confirmRemoveFarm"
      />
  
      <v-dialog
        v-model="nextConfirmDialog"
        max-width="480"
        @click:outside="cancelNextStep"
      >
        <v-card class="proceed-dialog-card">
          <v-btn icon small class="proceed-dialog__close" aria-label="Close" @click="cancelNextStep">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="text-center px-6 pt-7 pb-4">
            <div :style="proceedDialogIconWrapStyle">
              <v-icon size="30" :color="proceedDialogIconColor">{{ proceedDialogIcon }}</v-icon>
            </div>
            <div class="text-h6 font-weight-bold black--text mb-2">{{ proceedDialogTitle }}</div>
            <p class="text-body-2 grey--text text--darken-1 mb-3 text-left">{{ proceedDialogBody }}</p>
          </div>
          <v-card-actions class="px-5 pb-5 pt-1 d-flex flex-column" style="gap:10px;">
            <template v-if="proceedDialogMode === 'mitigation_required'">
              <v-btn depressed color="primary" block @click="openMitigationFromProceedDialog">
                {{ $t('dueDiligence.uploadMitigationDocumentation') }}
              </v-btn>
              <v-btn outlined color="primary" block @click="cancelAndCreateNewDds">
                {{ $t('dueDiligence.cancelAndCreateNewDds') }}
              </v-btn>
            </template>
            <template v-else-if="proceedDialogMode === 'mitigation_recommended'">
              <v-btn depressed color="primary" block @click="openMitigationFromProceedDialog">
                {{ $t('dueDiligence.uploadMitigationDocumentationRecommended') }}
              </v-btn>
              <v-btn outlined color="primary" block @click="proceedWithoutMitigation">
                {{ $t('dueDiligence.proceedWithoutMitigation') }}
              </v-btn>
            </template>
            <template v-else>
              <v-btn outlined color="primary" class="flex-grow-1" @click="cancelNextStep">{{ $t('dueDiligence.eudrAssessmentCannotProceedGoBack') }}</v-btn>
              <v-btn
                depressed
                color="primary"
                class="flex-grow-1"
                :disabled="blocksTrafficLightProceed"
                @click="confirmNextStep"
              >
                {{ blocksTrafficLightProceed ? $t('deforestation.complianceTrafficLightCannotProceed') : $t('deforestation.complianceTrafficLightYesProceed') }}
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-dialog v-model="removeConfirmDialog.open" max-width="380">
        <v-card style="border-radius:10px; position:relative;">
          <v-btn icon small class="proceed-dialog__close" aria-label="Close" @click="removeConfirmDialog.open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-card-title class="text-body-1 font-weight-bold pt-5 pb-2 d-flex align-center" style="gap:8px;">
            <v-icon :color="removeConfirmDialog.type === 'darkRed' ? 'error' : 'warning'">mdi-alert</v-icon>
            {{ $t('deforestation.complianceTrafficLightRemoveAllFarmsTitle', { riskLevel: removeConfirmRiskLevelLabel }) }}
          </v-card-title>
          <v-card-text class="pb-3">{{ $t('deforestation.complianceTrafficLightCannotBeUndone') }}</v-card-text>
          <v-card-actions class="px-4 pb-4 d-flex" style="gap:8px;">
            <v-btn outlined color="primary" class="flex-grow-1" @click="removeConfirmDialog.open = false">{{ $t('cancel') }}</v-btn>
            <v-btn :color="removeConfirmDialog.type !== 'yellow' ? 'error' : 'warning'" dark depressed class="flex-grow-1"
              :loading="!!bulkActionLoading" @click="executeRemoveFarms">{{ $t('deforestation.complianceTrafficLightYesRemove') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-dialog v-model="attachDocDialog.open" max-width="460" persistent>
        <v-card style="border-radius:12px; overflow:hidden;">
          <v-toolbar color="primary" dark dense elevation="0">
            <v-toolbar-title class="text-body-1 font-weight-bold">{{ $t('deforestation.complianceTrafficLightUploadMitigationDocument') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon small @click="attachDocDialog.open = false"><v-icon style="color:white!important">mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-5 pb-2">
            <v-checkbox
              v-if="unmitigatedLightRedCount > 0 || lightRedFarmCount > 0"
              v-model="attachDocDialog.selectedScopes"
              value="lightRed"
              hide-details
              class="mb-2"
            >
              <template #label><span class="text-body-2">{{ $t('deforestation.complianceTrafficLightHighRiskCount', { count: unmitigatedLightRedCount || lightRedFarmCount }) }}</span></template>
            </v-checkbox>
            <v-checkbox
              v-if="unmitigatedYellowCount > 0 || yellowFarmCount > 0"
              v-model="attachDocDialog.selectedScopes"
              value="yellow"
              hide-details
            >
              <template #label><span class="text-body-2">{{ $t('deforestation.complianceTrafficLightMediumRiskCount', { count: unmitigatedYellowCount || yellowFarmCount }) }}</span></template>
            </v-checkbox>
            <v-file-input v-model="attachDocDialog.file" class="mt-4" outlined dense accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" :label="$t('deforestation.complianceTrafficLightSelectDocument')" hide-details="auto" />
          </v-card-text>
          <v-card-actions class="px-4 pb-4 pt-3 d-flex" style="gap:8px;">
            <v-btn outlined color="primary" class="flex-grow-1" @click="attachDocDialog.open = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" depressed class="flex-grow-1" :disabled="!attachDocDialog.file || !attachDocDialog.selectedScopes.length"
              :loading="attachDocDialog.loading" @click="submitAttachDoc">{{ $t('deforestation.complianceTrafficLightAttachDocument') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="singleFarmAttachDialog.open" max-width="460" persistent>
        <v-card class="mitigation-dialog">
          <v-toolbar color="primary" dark dense elevation="0">
            <v-toolbar-title class="text-body-1 font-weight-bold white--text">{{ $t('deforestation.complianceTrafficLightUploadMitigationDocument') }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon small @click="singleFarmAttachDialog.open = false"><v-icon style="color:white!important">mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="mitigation-dialog__content">
            <v-alert
              v-if="singleFarmAttachDialog.revisionComment"
              type="warning"
              dense
              text
              class="mb-3"
            >
              <strong>{{ $t('deforestation.complianceTrafficLightRevisionRequested') }}</strong> {{ singleFarmAttachDialog.revisionComment }}
            </v-alert>
            <div v-if="singleFarmAttachDialog.existingFiles && singleFarmAttachDialog.existingFiles.length" class="mb-3">
              <div class="text-caption font-weight-medium mitigation-dialog__section-title">{{ $t('deforestation.complianceTrafficLightUploadedDocuments') }}</div>
              <div
                v-for="file in singleFarmAttachDialog.existingFiles"
                :key="file.id || file.file_path"
                class="mitigation-dialog__file-row"
              >
                <div class="text-caption text-truncate pr-2 mitigation-dialog__file-name">
                  {{ getMitigationFileName(file.file_path) }}
                </div>
                <div class="mitigation-dialog__file-actions">
                  <v-btn x-small text color="primary" class="mitigation-dialog__view-btn" @click="openMitigationFile(file.file_path)">
                    {{ $t('deforestation.complianceTrafficLightView') }}
                  </v-btn>
                  <v-btn
                    v-if="canRemoveMitigationFile(file)"
                    x-small
                    text
                    color="error"
                    class="mitigation-dialog__remove-btn"
                    :disabled="singleFarmAttachDialog.loading"
                    @click="removeSingleFarmMitigationDoc(file)"
                  >
                    {{ $t('deforestation.complianceTrafficLightRemove') }}
                  </v-btn>
                </div>
              </div>
            </div>
            <v-file-input v-model="singleFarmAttachDialog.file" outlined dense accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" :label="$t('deforestation.complianceTrafficLightSelectMitigationDocument')" hide-details="auto" :disabled="singleFarmAttachDialog.loading" />
            <v-progress-linear
              v-if="singleFarmAttachDialog.loading"
              :value="singleFarmAttachDialog.uploadProgress"
              height="6"
              color="primary"
              class="mt-3"
              rounded
            />
            <div v-if="singleFarmAttachDialog.loading" class="text-caption primary--text mt-1">
              {{ singleFarmAttachDialog.loadingMessage }}
            </div>
          </v-card-text>
          <v-card-actions class="mitigation-dialog__actions">
            <v-btn outlined color="primary" class="flex-grow-1" @click="singleFarmAttachDialog.open = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" class="flex-grow-1" depressed :disabled="!singleFarmAttachDialog.file"
              :loading="singleFarmAttachDialog.loading" @click="submitSingleFarmMitigationDoc">
              {{ singleFarmAttachDialog.existingFiles && singleFarmAttachDialog.existingFiles.length ? $t('deforestation.complianceTrafficLightUpdateDocument') : $t('deforestation.complianceTrafficLightUpload') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-dialog v-model="yellowWarningDialog.open" max-width="420">
        <v-card style="border-radius:12px; overflow:hidden; position:relative;">
          <v-btn icon small class="proceed-dialog__close" aria-label="Close" @click="yellowWarningDialog.open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="text-center px-6 pt-7 pb-4">
            <div class="text-h6 font-weight-bold black--text mb-2">{{ $t('deforestation.complianceTrafficLightMediumRiskWarning') }}</div>
            <p v-if="yellowWarningDialog.farmItem" class="text-body-2 grey--text text--darken-1">
              {{ $t('deforestation.complianceTrafficLightFarmMediumRisk', { name: yellowWarningDialog.farmItem.farm && yellowWarningDialog.farmItem.farm.farmName }) }}
            </p>
          </div>
          <v-card-actions class="px-5 pb-5 pt-1">
            <v-btn depressed color="primary" class="flex-grow-1" @click="yellowWarningDialog.open = false">{{ $t('deforestation.complianceTrafficLightGotIt') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-dialog v-if="trafficLightDialog" v-model="trafficLightDialog" max-width="900">
        <v-card class="tl-dialog">
          <div class="tl-dialog__header d-flex align-center px-5 py-3">
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold black--text">
                {{ trafficLightItem && trafficLightItem.farm && trafficLightItem.farm.farmName }}
              </div>
              <div class="text-caption grey--text">{{ $t("deforestation.complianceTrafficLight") }}</div>
            </div>
            <v-btn icon small @click="trafficLightDialog = false">
              <v-icon color="grey darken-1">mdi-close</v-icon>
            </v-btn>
          </div>
          <v-divider />
          <v-card-text class="pt-3 pb-4 px-5">
            <IYBTrafficLightPanel
              v-if="trafficLightItem"
              :key="'tl-panel-' + trafficLightMapKey"
              :traffic-light="trafficLightForPanel(trafficLightItem)"
              :show-mitigation="getTrafficLight(trafficLightItem) !== 'DARK_RED'"
              :farm="trafficLightItem.farm"
              :farm-type="trafficLightItem.farmType"
              :map-active="trafficLightDialog"
              :phase1-coming-soon="false"
              :commodity="trafficLightItem.commodity || reportCommodity"
              :phase1="trafficLightItem.phase1"
              :iyb-data="trafficLightItem.iybData"
              :diligence-report-id="diligenceId"
              :mitigation-submitting="trafficLightMitigationSubmitting"
              :existing-mitigation-files="getFarmMitigationFiles(trafficLightItem)"
              @submit-mitigation="submitTrafficLightPanelMitigation"
              @remove-existing-mitigation="removeTrafficLightMitigationDoc"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import loadingMixin from '@/mixins/LoadingMixin'
  import getPermittedActions from '@/mixins/GetPermittedActions'
  import reportReadonlyMixin from '@/mixins/reportReadonlyMixin'
  import DeforestationService from '@/_services/DeforestationService'
  import ConfirmBox from '@/components/ConfirmBox'
  import { mapGetters } from 'vuex'
  import { isDeforestationExpired } from '@/utils'
  import trafficLightAssessmentMixin from '../mixins/trafficLightAssessmentMixin'
  import IYBTrafficLightPanel from '../../approval-flow/components/IYBTrafficLightPanel.vue'
  import ProcessingDialog from '../components/ProcessingDialog.vue'
  import {
    derivePhase1FromProductionPlace,
    getRiskSnapshotFromProductionPlace,
    mapRiskSnapshotToIybData,
  } from '@/utils/trafficLightCalculator'
  import {
    findProductionPlaceForMitigation,
  } from '@/utils/ddsMitigationNotification'
  import {
    DDS_MITIGATION_SOURCE_TYPE,
    filterMitigationFilesBySource,
  } from '@/utils/ddsMitigationSourceType'
  
  export default {
    components: {
      ConfirmBox: ConfirmBox,
      IYBTrafficLightPanel,
      ProcessingDialog,
    },
    mixins: [loadingMixin, getPermittedActions, reportReadonlyMixin, trafficLightAssessmentMixin],
    props: {
      diligenceId: { type: Number, required: true },
    },
    data() {
      return {
        loading: true,
        search: '',
        statusModel: '',
        productionPlaces: [],
        totalProductionPlaces: 0,
        trafficLightMitigationSubmitting:'',
        from: 0,
        to: 0,
        options: { page: 1, limit: 10 },
        allStatus: [
          { text: this.$t('deforestation.allStatus'), val: '' },
          { text: this.$t('deforestation.veryHighProbability'), val: 'Very High Deforestation Probability' },
          { text: this.$t('deforestation.highProbability'), val: 'High Deforestation Probability' },
          { text: this.$t('deforestation.mediumProbability'), val: 'Medium Deforestation Probability' },
          { text: this.$t('deforestation.lowProbability'), val: 'Low Deforestation Probability' },
          { text: this.$t('deforestation.zeroOrNegligibleProbability'), val: 'Zero/Negligible Deforestation Probability' },
        ],
        tableHeaders: [
          { text: this.$t('deforestation.productionPlacesFarmAndFarmId'), value: 'farm.farmName', class: 'black--text', sortable: true },
          { text: this.$t('deforestation.producerName'), value: 'farm.userDdsAssoc.fullName', class: 'black--text', sortable: true },
          { text: this.$t('deforestation.addressOfProduction'), value: 'farm.address', class: 'black--text', sortable: true },
          { text: this.$t('deforestation.complianceTrafficLight'), value: 'trafficLight', align: 'center', class: 'black--text', sortable: false },
          { text: this.$t('dueDiligence.actions'), value: 'status', align: 'center', class: 'black--text', sortable: false, width: '100px' },
        ],
        farmConfirmDeleteDialog: false,
        farmConfirmDelete: {
          title: this.$t('confirmDialog.farm.delete.title'),
          message: this.$t('confirmDialog.farm.delete.message'),
          confirmBtnText: this.$t('confirmDialog.farm.delete.confirmBtn'),
          cancelBtnText: this.$t('cancel'),
          confirmData: {},
        },
      }
    },
    computed: {
      ...mapGetters('eudrSettings', ['get_EUDR_Settings']),
      isLocalDBVerificationEnable() {
        if (!this.get_EUDR_Settings?.local_db_verification_config) return false
        const parseVal = JSON.parse(this.get_EUDR_Settings.local_db_verification_config)
        return parseVal?.enabled || false
      },
      stepOffset() {
        return this.isLocalDBVerificationEnable ? 1 : 0
      },
      totalPages() {
        return parseInt(Math.ceil(this.totalProductionPlaces / this.options.limit))
      },
      filterData() {
        if (this.statusModel) {
          return this.productionPlaces.filter(item => item.eudr_deforestation_status === this.statusModel)
        }
        return this.productionPlaces
      },
      proceedDialogMode() {
        if (this.darkRedFarmCount > 0) return 'critical'
        if (this.unmitigatedLightRedCount > 0) return 'mitigation_required'
        if (this.unmitigatedYellowCount > 0) return 'mitigation_recommended'
        return 'ready'
      },
      proceedDialogTitle() {
        if (this.proceedDialogMode === 'critical') return this.$t('deforestation.complianceTrafficLightCannotProceed')
        if (this.proceedDialogMode === 'mitigation_required') {
          return this.$t('dueDiligence.riskAdvisoryMitigationRequired')
        }
        if (this.proceedDialogMode === 'mitigation_recommended') {
          return this.$t('dueDiligence.riskAdvisoryMitigationRecommended')
        }
        return this.$t('deforestation.complianceTrafficLightReadyToProceedTitle')
      },
      proceedDialogBody() {
        if (this.proceedDialogMode === 'critical') {
          return this.$t('deforestation.complianceTrafficLightCriticalRiskProceedBody', {
            count: this.darkRedFarmCount,
          })
        }
        if (this.proceedDialogMode === 'mitigation_required') {
          return this.$t('dueDiligence.riskAdvisoryMitigationRequiredBody', {
            count: this.unmitigatedLightRedCount,
          })
        }
        if (this.proceedDialogMode === 'mitigation_recommended') {
          return this.$t('dueDiligence.riskAdvisoryMitigationRecommendedBody', {
            count: this.unmitigatedYellowCount,
          })
        }
        return this.$t('deforestation.complianceTrafficLightAllFarmsGoodBody')
      },
      proceedDialogIcon() {
        if (this.proceedDialogMode === 'critical') return 'mdi-close-octagon'
        if (this.proceedDialogMode === 'mitigation_required') return 'mdi-alert-circle-outline'
        if (this.proceedDialogMode === 'mitigation_recommended') return 'mdi-alert-circle-outline'
        return 'mdi-check-circle-outline'
      },
      proceedDialogIconColor() {
        if (this.proceedDialogMode === 'critical') return 'error'
        if (this.proceedDialogMode === 'mitigation_required') return 'error'
        if (this.proceedDialogMode === 'mitigation_recommended') return 'warning'
        return 'success'
      },
      proceedDialogIconWrapStyle() {
        const bg = {
          critical: '#FFEBEE',
          mitigation_required: '#FFEBEE',
          mitigation_recommended: '#FFF3E0',
          ready: '#E8F5E9',
        }[this.proceedDialogMode] || '#E8F5E9'
        return `width:56px; height:56px; border-radius:50%; background:${bg}; margin:0 auto 14px; display:flex; align-items:center; justify-content:center;`
      },
      removeConfirmRiskLevelLabel() {
        const type = this.removeConfirmDialog.type
        if (type === 'darkRed') return this.$t('deforestation.complianceTrafficLightLegendCriticalRisk')
        if (type === 'lightRed') return this.$t('deforestation.riskStatusHigh')
        return this.$t('deforestation.combinedComplianceMediumRisk')
      },
    },
    async mounted() {
      await this.loadReportCommodity()
      this.resumeWildfireBulkIfPending()
      await this.getProductionPlaces()
      this.$store.dispatch('SET_DDS_CURRENT_TAB_TO_DB', {
        step: 3,
        reportId: Number(this.diligenceId),
      })
      await this.handleMitigationDeepLink()
    },
    methods: {
      parseMitigationDeepLinkQuery() {
        const query = this.$route?.query || {}
        if (query.openMitigation !== '1') return null

        if (query.mitigationContext === 'eudr_deforestation') return null

        const toInt = (value) => {
          if (value == null || value === '') return null
          const parsed = parseInt(value, 10)
          return Number.isNaN(parsed) ? null : parsed
        }

        return {
          farmId: toInt(query.mitigationFarmId),
          productionPlaceId: toInt(query.mitigationProductionPlaceId),
          diligenceReportProductionPlaceId: toInt(query.mitigationDrppId),
          revisionComment: query.mitigationRevisionComment || '',
        }
      },
      clearMitigationDeepLinkQuery() {
        const query = { ...this.$route.query }
        delete query.openMitigation
        delete query.mitigationContext
        delete query.mitigationFarmId
        delete query.mitigationProductionPlaceId
        delete query.mitigationDrppId
        delete query.mitigationRevisionComment
        this.$router.replace({ query }).catch(() => {})
      },
      async findProductionPlaceForMitigationDeepLink(criteria) {
        let item = findProductionPlaceForMitigation(this.productionPlaces, criteria)
        if (item) return item

        const originalPage = this.options.page
        const originalLimit = this.options.limit
        const pageSize = 100

        this.options.page = 1
        this.options.limit = pageSize
        await this.getProductionPlaces()
        item = findProductionPlaceForMitigation(this.productionPlaces, criteria)
        if (item) return item

        const totalPages = Math.max(1, Math.ceil(this.totalProductionPlaces / pageSize))
        for (let page = 2; page <= totalPages; page += 1) {
          this.options.page = page
          await this.getProductionPlaces()
          item = findProductionPlaceForMitigation(this.productionPlaces, criteria)
          if (item) return item
        }

        this.options.page = originalPage
        this.options.limit = originalLimit
        await this.getProductionPlaces()
        return null
      },
      async handleMitigationDeepLink() {
        const criteria = this.parseMitigationDeepLinkQuery()
        if (!criteria) return

        const hasTarget = criteria.farmId
          || criteria.productionPlaceId
          || criteria.diligenceReportProductionPlaceId
        if (!hasTarget) {
          this.clearMitigationDeepLinkQuery()
          return
        }

        const farmItem = await this.findProductionPlaceForMitigationDeepLink(criteria)
        this.clearMitigationDeepLinkQuery()

        if (!farmItem) {
          this.$notify({
            text: this.$t('deforestation.complianceTrafficLightMitigationFarmNotFound'),
            type: 'warning',
          })
          return
        }

        this.$nextTick(() => {
          this.openSingleFarmMitigation(farmItem, { revisionComment: criteria.revisionComment })
        })
      },
      hasMitigationDocs(item) {
        const files = this.getFarmMitigationFiles(item)
        return filterMitigationFilesBySource(files, DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE).length > 0
          || filterMitigationFilesBySource(files, 'other').length > 0
      },
      async enrichProductionPlacesWithTrafficLight(rows) {
        const mitigatedIds = []

        const enrichedRows = rows.map((item) => {
          const riskSnapshot = getRiskSnapshotFromProductionPlace(item)
          const iybData = item.iybTrafficLightData
            || (riskSnapshot ? mapRiskSnapshotToIybData(riskSnapshot) : null)
          const phase1 = derivePhase1FromProductionPlace(item)
          const commodity = item.commodity || this.reportCommodity
          const computedTrafficLight = item.computedTrafficLight
            || riskSnapshot?.trafficLight
            || null
          const hasMitigationDocs = item.hasMitigationDocs ?? this.hasMitigationDocs(item)

          if (hasMitigationDocs) {
            mitigatedIds.push(item.id)
          }

          return {
            ...item,
            kabProfileId: item.kabProfileId ?? item.farm?.kabProfileId ?? riskSnapshot?.kabProfileId ?? null,
            iybData,
            riskSnapshot,
            usingFrozenRiskSnapshot: item.usingFrozenRiskSnapshot ?? Boolean(riskSnapshot),
            iybScoreMissing: item.iybScoreMissing ?? (!riskSnapshot && item.farm?.kabProfileId != null && !iybData),
            regencyMissing: item.regencyMissing ?? (item.farm?.kabProfileId == null && riskSnapshot?.kabProfileId == null),
            phase1,
            commodity,
            computedTrafficLight,
            hasMitigationDocs,
          }
        })

        if (this.trafficLightSummary?.mitigatedProductionPlaceIds?.length) {
          this.mitigatedFarmIds = [...this.trafficLightSummary.mitigatedProductionPlaceIds]
        } else {
          this.mitigatedFarmIds = mitigatedIds
        }
        return enrichedRows
      },
      async triggerSubmit() {
        this.startLoading()
        try {
          await this.refreshTrafficLightSummary()
        } catch (error) {
          console.error('Failed to refresh traffic light summary', error)
          this.$notify({ text: this.$t('somethingWentWrong'), type: 'error' })
          return false
        } finally {
          this.stopLoading()
        }

        const confirmed = await this.confirmTrafficLightNextStep()
        if (!confirmed) return false
        if (!this.canProceedFromTrafficLightStep) return false
        return true
      },
      searchQuery() {
        this.getProductionPlaces()
      },
      pageChange(forward) {
        this.options.page = forward ? this.options.page + 1 : this.options.page - 1
        this.getProductionPlaces()
      },
      removeFarm(id) {
        this.farmConfirmDelete.confirmData = { id }
        this.farmConfirmDeleteDialog = true
      },
      async confirmRemoveFarm(action, confirmData) {
        this.startLoading()
        try {
          await DeforestationService.removeFarmFromProductionPlaceListById({
            diligenceReportId: this.diligenceId,
            farmId: confirmData.id,
          })
          await this.getProductionPlaces()
          this.farmConfirmDeleteDialog = false
        } catch (e) {
          console.error(e)
          this.$notify({ title: this.$t('somethingWentWrong'), type: 'error' })
        } finally {
          this.stopLoading()
        }
      },
      async getProductionPlaces() {
        this.loading = true
        const requestParams = {
          page: this.options.page,
          limit: this.options.limit,
          diligenceReportId: this.diligenceId,
          removed: false,
          searchPhrase: this.search,
          eudrDeforestationStatus: this.statusModel,
        }
        try {
          const res = await DeforestationService.getProductionPlaces(requestParams)
          let rows = res.data.productionPlaces.rows.map((item) => {
            const deforestationInfo = item.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo
            return {
              ...item,
              deforestationInfo,
              isExpired: isDeforestationExpired(deforestationInfo?.deforestationStatusDate, this.get_EUDR_Settings),
            }
          })
          rows = await this.enrichProductionPlacesWithTrafficLight(rows)
          this.productionPlaces = rows
          this.trafficLightSummary = res.data.productionPlaces.trafficLightSummary || null
          this.totalProductionPlaces = res.data.productionPlaces.totalCount
          this.from = (this.options.page - 1) * this.options.limit + 1
          this.to = Math.min(this.options.page * this.options.limit, this.totalProductionPlaces)
          if (this.totalProductionPlaces === 0) this.from = 0
        } catch (err) {
          console.error(err)
        } finally {
          this.loading = false
        }
      },
    },
  }
  </script>
  
  <style lang="scss" scoped>
  .proceed-dialog-card {
    border-radius: 12px;
    overflow: hidden;
    position: relative;
  }

  .proceed-dialog__close {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
  }

  .traffic-light-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    padding: 12px 16px;
    background: #FAFAFA;
    border-radius: 8px;
    border: 1px solid #E0E0E0;
  }

  .traffic-light-legend__entry {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .traffic-light-legend__marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    overflow: hidden;
  }

  .traffic-light-legend__marker--badge {
    border-radius: 50%;
  }

  .traffic-light-legend ::v-deep .traffic-light-legend__marker-icon.v-icon {
    font-size: 20px !important;
    width: 20px !important;
    height: 20px !important;
    line-height: 20px !important;
  }

  .traffic-light-legend ::v-deep .traffic-light-legend__marker-icon--inverse.v-icon {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
    line-height: 12px !important;
    color: #fff !important;
  }

  .traffic-light-legend__divider {
    width: 1px;
    height: 20px;
    background: #e0e0e0;
    flex-shrink: 0;
  }

  .traffic-light-legend__marker--yellow {
    background: #ffa826;
  }

  .traffic-light-legend__marker--red {
    background: #da3d3d;
  }

  .traffic-light-legend__marker--green {
    background: #489e16;
  }
  
  .filter-controls-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  }
  
  .filter-controls-left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .filter-field--search { width: 200px; }
  .filter-field--status { width: 220px; }
  
  .filter-controls-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .pagination-info {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }
  
  .production-place-actions-grid {
    display: grid;
    grid-template-columns: 32px 32px;
    gap: 4px;
    justify-content: center;
    align-items: center;
  }
  
  .production-place-actions__cell {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
  }
  
  .production-place-actions__btn {
    width: 28px !important;
    height: 28px !important;
  }
  
  .tl-dialog {
    border-radius: 12px;
    overflow: hidden;
  }
  
  .tl-dialog__header {
    background: #fff;
  }

  .mitigation-dialog {
    border-radius: 12px;
    overflow: hidden;
  }

  .mitigation-dialog__content {
    padding: 16px 18px 8px !important;
  }

  .mitigation-dialog__section-title {
    color: #616161;
    letter-spacing: 0.2px;
  }

  .mitigation-dialog__file-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #fff;
  }

  .mitigation-dialog__file-name {
    max-width: 220px;
    color: #424242;
  }

  .mitigation-dialog__file-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .mitigation-dialog__view-btn {
    min-width: 48px !important;
    font-weight: 600;
  }

  .mitigation-dialog__remove-btn {
    min-width: 56px !important;
    font-weight: 600;
  }

  .mitigation-dialog__actions {
    display: flex;
    gap: 10px;
    padding: 8px 18px 18px !important;
  }
  </style>
  